import { useState, useEffect, useCallback } from 'react';
import { adminOrderService, AdminOrderQuery, OrderAlert, AdminOrderStatusCounts } from '@services/adminOrderService';
import type { Order } from '@/types';

const EMPTY_STATUS_COUNTS: AdminOrderStatusCounts = {
  CREATED: 0,
  PREPARING: 0,
  READY: 0,
  ON_WAY: 0,
  DELIVERED: 0,
  CANCELLED: 0,
};

export function useAdminOrders(query: AdminOrderQuery) {
  const [orders, setOrders] = useState<Order[]>([]);
  const [total, setTotal] = useState(0);
  const [statusCounts, setStatusCounts] = useState<AdminOrderStatusCounts>(EMPTY_STATUS_COUNTS);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const refetch = useCallback(() => {
    setRefreshKey((current) => current + 1);
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadOrders = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const result = await adminOrderService.listAllOrders(query);
        if (isMounted) {
          setOrders(result.orders);
          setTotal(result.total);
          setStatusCounts(result.statusCounts ?? EMPTY_STATUS_COUNTS);
        }
      } catch (err: any) {
        if (isMounted) {
          setError(err.response?.data?.error?.message || 'Siparişler yüklenemedi');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadOrders();

    return () => {
      isMounted = false;
    };
  }, [
    query.status,
    query.restaurantId,
    query.courierId,
    query.startDate,
    query.endDate,
    query.page,
    query.limit,
    query.search,
    query.unassignedOnly,
    query.minWaitMinutes,
    refreshKey,
  ]);

  return { orders, total, statusCounts, isLoading, error, refetch };
}

export function useOrderAlerts() {
  const [alerts, setAlerts] = useState<OrderAlert[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const loadAlerts = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const result = await adminOrderService.getOrderAlerts();
        if (isMounted) {
          setAlerts(result);
        }
      } catch (err: any) {
        if (isMounted) {
          setError(err.response?.data?.error?.message || 'Uyarılar yüklenemedi');
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadAlerts();

    const interval = setInterval(loadAlerts, 60000);

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, []);

  return { alerts, isLoading, error };
}
